<template>
  <v-card elevation="3" class="my-5 card-fixed-height">
    <v-card-title class="card-header py-3 px-4">
      <v-icon color="catGreen" class="mr-2 flex-shrink-0">mdi-human</v-icon>
      <span class="card-title-text">{{ infoTitle }}</span>
      <!-- salvia-781/878: atalho de inserção, sempre visível. Abre o diálogo
           direto no formulário; o botão ao lado abre o histórico. -->
      <v-tooltip v-if="showQuickAdd" bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            class="flex-shrink-0 ml-1 card-quick-add"
            :aria-label="`Adicionar em ${infoTitle}`"
            @click="openQuickAdd"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>Adicionar sem abrir o histórico</span>
      </v-tooltip>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            class="flex-shrink-0 ml-1"
            @click="openDialog"
          >
            <v-icon data-tour="pd-card-menu">mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <span>Ver histórico e editar</span>
      </v-tooltip>
    </v-card-title>

    <v-divider></v-divider>

    <!-- v-menu com open-on-hover para popover flutuante ao lado do card -->
    <v-menu
      v-if="latest"
      open-on-hover
      :close-on-content-click="false"
      offset-x
      right
      nudge-right="8"
      transition="slide-x-transition"
      max-width="520"
      content-class="body-composition-popover"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-card-text
          class="pa-4 compact-content"
          v-bind="attrs"
          v-on="on"
        >
          <!-- Visualização compacta: IMC destacado + Peso + Altura -->
          <div class="imc-hero">
            <div class="imc-value" :style="{ color: imcColor }">
              {{ fmt(latest.bmi) }}
            </div>
            <div class="imc-label">IMC</div>
            <div v-if="imcClassification" class="imc-classification">
              {{ imcClassification }}
            </div>
          </div>

          <v-divider class="my-3"></v-divider>

          <v-row dense>
            <v-col cols="6">
              <div class="mini-metric">
                <v-icon small color="primary" class="mr-1">mdi-weight</v-icon>
                <span class="mini-label">Peso</span>
                <span class="mini-value">
                  {{ fmt(latest.weight_kg) }}<small> kg</small>
                </span>
              </div>
            </v-col>
            <v-col cols="6">
              <div class="mini-metric">
                <v-icon small color="primary" class="mr-1">
                  mdi-human-male-height
                </v-icon>
                <span class="mini-label">Altura</span>
                <span class="mini-value">
                  {{ fmt(latest.height_cm) }}<small> cm</small>
                </span>
              </div>
            </v-col>
          </v-row>

          <div class="hover-hint">
            <v-icon x-small color="grey">mdi-gesture-tap</v-icon>
            <span>Passe o mouse para ver todos os dados</span>
          </div>
        </v-card-text>
      </template>

      <!-- Conteúdo do popover -->
      <v-card class="popover-card" elevation="8">
        <v-card-title
          class="py-2 px-4 d-flex align-center white--text popover-header"
        >
          <v-icon color="white" small class="mr-2">mdi-human</v-icon>
          <span class="text-subtitle-2 font-weight-medium">
            Composição Corporal — Detalhes
          </span>
        </v-card-title>

        <v-card-text class="pa-4">
          <v-row dense>
            <v-col cols="6" sm="4">
              <MetricBox label="Peso" :value="latest.weight_kg" suffix="kg" />
            </v-col>
            <v-col cols="6" sm="4">
              <MetricBox label="Altura" :value="latest.height_cm" suffix="cm" />
            </v-col>
            <v-col cols="6" sm="4">
              <MetricBox
                label="IMC"
                :value="latest.bmi"
                :sub-label="imcClassification"
              />
            </v-col>

            <v-col cols="6" sm="4">
              <MetricBox
                label="% Gordura"
                :value="latest.fat_percent"
                suffix="%"
              />
            </v-col>
            <v-col cols="6" sm="4">
              <MetricBox
                label="% Massa Magra"
                :value="latest.lean_mass_percent"
                suffix="%"
              />
            </v-col>
            <v-col cols="6" sm="4">
              <MetricBox label="Gordura" :value="latest.fat_kg" suffix="kg" />
            </v-col>

            <v-col cols="6" sm="4">
              <MetricBox
                label="Massa Magra"
                :value="latest.lean_mass_kg"
                suffix="kg"
              />
            </v-col>
            <v-col cols="6" sm="4">
              <MetricBox label="Cintura" :value="latest.waist_cm" suffix="cm" />
            </v-col>
            <v-col cols="6" sm="4">
              <MetricBox label="Quadril" :value="latest.hip_cm" suffix="cm" />
            </v-col>
          </v-row>

          <v-divider class="my-3"></v-divider>

          <v-row dense>
            <v-col cols="12" sm="6">
              <div class="ratio-box">
                <div class="ratio-label">Índice Cintura/Estatura</div>
                <div class="ratio-value" :style="{ color: waistHeightColor }">
                  {{ formatRatio(latest.waist_height_ratio) }}
                </div>
                <div class="ratio-hint">Ideal: abaixo de 0,50</div>
              </div>
            </v-col>
            <v-col cols="12" sm="6">
              <div class="ratio-box">
                <div class="ratio-label">Razão Cintura/Quadril</div>
                <div class="ratio-value" :style="{ color: waistHipColor }">
                  {{ formatRatio(latest.waist_hip_ratio) }}
                </div>
                <div class="ratio-hint">
                  Ideal: abaixo de {{ waistHipThreshold.toFixed(2) }}
                  ({{ genderLabel }})
                </div>
              </div>
            </v-col>
          </v-row>

          <div class="text-caption text--secondary mt-3 text-right">
            <v-icon small color="grey">mdi-clock-outline</v-icon>
            Última medição: {{ formatDate(latest.created_at) }}
          </div>
        </v-card-text>
      </v-card>
    </v-menu>

    <!-- Estado vazio (sem menu) -->
    <v-card-text v-else class="pa-4 compact-content">
      <div class="text-center py-6">
        <v-icon size="48" color="grey lighten-1">mdi-scale-bathroom</v-icon>
        <p class="text-caption grey--text mt-2 mb-0">
          Nenhuma medida registrada ainda.
        </p>
        <v-btn
          small
          color="catGreen"
          outlined
          class="mt-3"
          @click="openQuickAdd"
        >
          <v-icon small left>mdi-plus</v-icon>
          Adicionar medida
        </v-btn>
      </div>
    </v-card-text>

    <BodyCompositionDialog
      v-model="dialogVisible"
      :open-direct-add="quickAddMode"
      :patient-id="patientId"
      :gender="gender"
      @data-saved="handleDataSaved"
      @data-deleted="handleDataChanged"
    />
  </v-card>
</template>

<script>
import http from "@/services/http";
import BodyCompositionDialog from "@/features/patients/profile-dashboard/dialogs/BodyCompositionDialog.vue";

// Mini-componente para métricas no popover (render function, evita template compiler)
const MetricBox = {
  props: {
    label: String,
    value: [String, Number],
    suffix: { type: String, default: "" },
    subLabel: { type: String, default: "" },
  },
  computed: {
    /** SALVIA-938: o card mostra só a última medição, não o histórico — sem contador. */
    itemCount() {
      return null;
    },
    displayValue() {
      if (this.value === null || this.value === undefined || this.value === "")
        return "—";
      const num = parseFloat(this.value);
      return isNaN(num) ? "—" : num.toString().replace(".", ",");
    },
  },
  render(h) {
    const children = [
      h("div", { class: "metric-label" }, this.label),
      h("div", { class: "metric-value" }, [
        this.displayValue,
        this.suffix && this.displayValue !== "—"
          ? h("span", { class: "metric-suffix" }, " " + this.suffix)
          : null,
      ]),
    ];
    if (this.subLabel) {
      children.push(h("div", { class: "metric-sub" }, this.subLabel));
    }
    return h("div", { class: "metric-box" }, children);
  },
};

import patientDataRefresh from "@/features/patients/profile-dashboard/mixins/patientDataRefresh";
import { formatDateTime } from "@/utils/datetime";
import cardQuickAdd from "@/features/patients/profile-dashboard/mixins/cardQuickAdd.js";
import cardItemCount from "@/features/patients/profile-dashboard/mixins/cardItemCount.js";

export default {
  mixins: [patientDataRefresh, cardQuickAdd, cardItemCount],
  name: "BodyCompositionCard",

  components: {
    BodyCompositionDialog,
    MetricBox,
  },

  props: {
    patientId: {
      type: [String, Number],
      required: true,
    },
    // O título vem de config/dashboardCards.js, via a página do dashboard.
    // Sem default de propósito: duplicar o texto aqui já fez a renomeação
    // do card passar despercebida uma vez.
    infoTitle: { type: String, required: true },
  },

  data() {
    return {
      latest: null,
      gender: null,
      dialogVisible: false,
    };
  },

  computed: {
    isMale() {
      return (this.gender || "").toLowerCase().startsWith("masc");
    },
    waistHipThreshold() {
      return this.isMale ? 0.9 : 0.85;
    },
    genderLabel() {
      return this.isMale ? "homens" : "mulheres";
    },
    waistHeightColor() {
      const v = parseFloat(this.latest?.waist_height_ratio);
      if (isNaN(v)) return "#9e9e9e";
      return v < 0.5 ? "var(--ds-primary)" : "var(--ds-warning)";
    },
    waistHipColor() {
      const v = parseFloat(this.latest?.waist_hip_ratio);
      if (isNaN(v)) return "#9e9e9e";
      return v < this.waistHipThreshold ? "var(--ds-primary)" : "var(--ds-warning)";
    },
    imcClassification() {
      const v = parseFloat(this.latest?.bmi);
      if (isNaN(v)) return "";
      if (v < 16) return "Magreza grave";
      if (v < 17) return "Magreza moderada";
      if (v < 18.5) return "Magreza leve";
      if (v < 25) return "Saudável";
      if (v < 30) return "Sobrepeso";
      if (v < 35) return "Obesidade I";
      if (v < 40) return "Obesidade II";
      return "Obesidade III";
    },
    imcColor() {
      const v = parseFloat(this.latest?.bmi);
      if (isNaN(v)) return "#9e9e9e";
      if (v < 18.5) return "#FFA000";
      if (v < 25) return "#2E7D32";
      if (v < 30) return "#F57C00";
      return "#D84315";
    },
  },

  watch: {
    patientId: "fetchData",
  },

  created() {
    this.fetchData().finally(() => this.$emit("card-loaded"));
  },

  methods: {
    formatDate: formatDateTime,
    async fetchData() {
      if (!this.patientId) return;
      try {
        const { data } = await http.get(
          `/api/body-composition/latest/${this.patientId}`,
        );
        if (data?.status === "success") {
          this.latest = data.data?.record || null;
          this.gender = data.data?.gender || null;
        }
      } catch (err) {
        console.error("Erro ao buscar composição corporal:", err);
      }
    },

    openDialog() {
      this.dialogVisible = true;
    },

    async handleDataChanged() {
      await this.fetchData();
      this.$emit("data-updated");
    },

    formatRatio(v) {
      if (v === null || v === undefined || v === "") return "—";
      const num = parseFloat(v);
      return isNaN(num) ? "—" : num.toFixed(2).replace(".", ",");
    },

    fmt(v) {
      if (v === null || v === undefined || v === "") return "—";
      const n = parseFloat(v);
      return isNaN(n) ? "—" : n.toString().replace(".", ",");
    },
  },
};
</script>

<style scoped>
/* ═══ Tema: Verde (Composição Corporal) ═══ */
.card-fixed-height {
  display: flex;
  flex-direction: column;
  height: 320px;
  border-radius: 14px !important;
  border: 1px solid var(--ds-green-100) !important;
  box-shadow: 0 2px 10px rgba(22, 163, 74, 0.10) !important;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.card-fixed-height:hover {
  border-color: #86EFAC !important;
  box-shadow: 0 6px 20px rgba(22, 163, 74, 0.18) !important;
}

/* ═══ Header com gradient sutil ═══ */
.card-header {
  display: flex !important;
  flex-wrap: nowrap !important;
  align-items: center !important;
  min-width: 0;
  background: linear-gradient(135deg, var(--ds-green-bg) 0%, #DCFCE7 100%);
  border-bottom: 1px solid var(--ds-green-100);
}

.card-title-text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.4;
  color: var(--ds-green-700);
  letter-spacing: 0.3px;
}

.compact-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.compact-content:hover {
  background-color: var(--ds-green-bg);
}

.imc-hero {
  text-align: center;
  padding: 4px 0;
}

.imc-value {
  font-size: 44px;
  font-weight: 800;
  line-height: 1;
  margin-bottom: 4px;
  letter-spacing: -1px;
  transition: color 0.3s ease;
}

.imc-label {
  font-size: 10px;
  font-weight: 700;
  color: var(--ds-green-700);
  text-transform: uppercase;
  letter-spacing: 1px;
}

.imc-classification {
  font-size: 12px;
  font-weight: 600;
  color: var(--ds-text-600);
  margin-top: 4px;
}

.mini-metric {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background-color: var(--ds-green-bg);
  border: 1px solid var(--ds-green-100);
  border-radius: 8px;
  font-size: 12px;
}

.mini-label {
  color: var(--ds-green-700);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-right: 6px;
  flex: 1;
  font-size: 10px;
}

.mini-value {
  font-weight: 700;
  color: var(--ds-text-strong);
}

.mini-value small {
  font-weight: 500;
  color: var(--ds-text-muted);
  font-size: 10px;
}

.hover-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 10px;
  color: var(--ds-text-muted);
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px dashed var(--ds-green-100);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-weight: 500;
}
</style>

<!-- Sem scoped: v-menu renderiza fora do componente -->
<style>
.body-composition-popover {
  box-shadow: 0 12px 32px rgba(22, 163, 74, 0.22),
    0 4px 12px rgba(0, 0, 0, 0.06) !important;
  border-radius: 12px !important;
  overflow: hidden;
  animation: popoverFadeIn 0.2s ease-out;
  /* Acima do layout do atendimento (form/cards usam z-index 200), senão o
     popover de hover fica escondido atrás no modo "novo atendimento". */
  z-index: 260 !important;
}

@keyframes popoverFadeIn {
  from { opacity: 0; transform: translateX(-8px); }
  to   { opacity: 1; transform: translateX(0); }
}

/* Header verde do popover (substitui v-toolbar primary) */
.popover-card .popover-header {
  background: linear-gradient(135deg, #16A34A 0%, var(--ds-green-700) 100%) !important;
}

.popover-card .metric-box {
  padding: 8px 6px;
  text-align: center;
  border-radius: 8px;
  background-color: var(--ds-green-bg);
  border: 1px solid var(--ds-green-100);
  height: 100%;
}

.popover-card .metric-label {
  font-size: 10px;
  font-weight: 700;
  color: var(--ds-green-700);
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.popover-card .metric-value {
  font-size: 16px;
  font-weight: 800;
  color: var(--ds-text-strong);
  margin-top: 2px;
}

.popover-card .metric-suffix {
  font-size: 11px;
  font-weight: 500;
  color: var(--ds-text-muted);
}

.popover-card .metric-sub {
  font-size: 10px;
  color: var(--ds-text-500);
  margin-top: 2px;
}

.popover-card .ratio-box {
  padding: 12px;
  text-align: center;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--ds-green-bg) 0%, #DCFCE7 100%);
  border: 1px solid var(--ds-green-100);
}

.popover-card .ratio-label {
  font-size: 10px;
  font-weight: 700;
  color: var(--ds-green-700);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.popover-card .ratio-value {
  font-size: 22px;
  font-weight: 800;
  margin-top: 2px;
  letter-spacing: -0.3px;
}

.popover-card .ratio-hint {
  font-size: 10px;
  color: var(--ds-text-muted);
  margin-top: 2px;
  font-weight: 500;
}
</style>